// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback } from 'react';
import {
  ActivityIndicator,
  FlatList,
  type ListRenderItem,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { LeaderboardColumnHeader } from './LeaderboardColumnHeader';
import { LeaderboardEmptyState } from './LeaderboardEmptyState';
import { LeaderboardEntryRow } from './LeaderboardEntryRow';
import { LeaderboardTemplatePicker } from './LeaderboardTemplatePicker';
import { leaderboardStrings } from './strings';
import type {
  LeaderboardEntry,
  LeaderboardId,
  LeaderboardLoader,
  LeaderboardSubscriber,
  LeaderboardTemplate,
  LeaderboardTemplateLoader,
} from './types';
import { useLeaderboardViewModel } from './useLeaderboardViewModel';

export interface LeaderboardScreenProps {
  readonly initialBoardId: LeaderboardId | null;
  readonly loadBoard: LeaderboardLoader;
  readonly subscribeBoard: LeaderboardSubscriber;
  readonly loadTemplates: LeaderboardTemplateLoader;
  readonly onAddEntry?: (() => void) | undefined;
  readonly onAddColumn?: (() => void) | undefined;
  readonly onUseTemplate?: ((template: LeaderboardTemplate) => void) | undefined;
  readonly onStartBlank?: (() => void) | undefined;
  readonly onOpenOptions?: (() => void) | undefined;
}

const ROW_HEIGHT_ESTIMATE = 41;

export const LeaderboardScreen: React.FC<LeaderboardScreenProps> = ({
  initialBoardId,
  loadBoard,
  subscribeBoard,
  loadTemplates,
  onAddEntry,
  onAddColumn,
  onUseTemplate,
  onStartBlank,
  onOpenOptions,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');
  const vm = useLeaderboardViewModel({
    initialBoardId,
    loadBoard,
    subscribeBoard,
    loadTemplates,
  });

  const keyExtractor = useCallback((entry: LeaderboardEntry) => entry.id, []);

  const populated = vm.state.kind === 'populated' ? vm.state : null;

  const renderItem: ListRenderItem<LeaderboardEntry> = useCallback(
    ({ item }) => <LeaderboardEntryRow entry={item} columns={populated?.board.columns ?? []} />,
    [populated],
  );

  const getItemLayout = useCallback(
    (_: ArrayLike<LeaderboardEntry> | null | undefined, index: number) => ({
      length: ROW_HEIGHT_ESTIMATE,
      offset: ROW_HEIGHT_ESTIMATE * index,
      index,
    }),
    [],
  );

  const renderTitle = (): string => {
    if (vm.state.kind === 'populated') {
      return vm.state.board.title;
    }
    return leaderboardStrings.screenTitle;
  };

  const renderBody = (): React.ReactElement => {
    if (vm.state.kind === 'loading') {
      return (
        <View style={styles.center} accessibilityRole="progressbar">
          <ActivityIndicator size="small" color={theme.colors.accent.indigo} />
        </View>
      );
    }
    if (vm.state.kind === 'error') {
      return (
        <View style={styles.center}>
          <Text style={[styles.errorTitle, { color: theme.colors.text.primary }]}>
            {leaderboardStrings.errorTitle}
          </Text>
          <Pressable
            onPress={vm.retry}
            accessibilityRole="button"
            accessibilityLabel={leaderboardStrings.errorRetry}
            style={({ pressed }) => [
              styles.retryButton,
              { backgroundColor: theme.colors.accent.indigo },
              pressed && { opacity: 0.85 },
            ]}
          >
            <Text style={styles.retryText}>{leaderboardStrings.errorRetry}</Text>
          </Pressable>
        </View>
      );
    }
    if (vm.state.kind === 'empty') {
      if (vm.templates.length === 0) {
        return <LeaderboardEmptyState />;
      }
      return (
        <LeaderboardTemplatePicker
          templates={vm.templates}
          selectedTemplateId={vm.selectedTemplateId}
          onSelect={(t) => vm.selectTemplate(t?.id ?? null)}
          onUseTemplate={(t) => onUseTemplate?.(t)}
          onStartBlank={() => onStartBlank?.()}
        />
      );
    }

    return (
      <ScrollView horizontal showsHorizontalScrollIndicator>
        <View>
          <View style={styles.headerRow}>
            {vm.state.board.columns.map((column) => (
              <LeaderboardColumnHeader
                key={column.id}
                column={column}
                sort={vm.state.kind === 'populated' ? vm.state.sort : null}
                onPress={(c) => vm.setSort(c.id)}
              />
            ))}
          </View>
          <FlatList<LeaderboardEntry>
            data={vm.state.entries}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            getItemLayout={getItemLayout}
            windowSize={10}
            initialNumToRender={20}
            maxToRenderPerBatch={20}
            removeClippedSubviews={false}
          />
        </View>
      </ScrollView>
    );
  };

  return (
    <View
      style={[styles.container, { backgroundColor: theme.colors.surface.primary }]}
      accessibilityLabel={leaderboardStrings.screenTitle}
    >
      <View style={[styles.header, { borderBottomColor: theme.colors.border.secondary }]}>
        <Text style={[styles.title, { color: theme.colors.text.primary }]} numberOfLines={1}>
          {renderTitle()}
        </Text>
        <View style={styles.headerActions}>
          {populated !== null && onAddColumn !== undefined ? (
            <Pressable
              onPress={onAddColumn}
              accessibilityRole="button"
              accessibilityLabel={leaderboardStrings.addColumnCta}
              style={({ pressed }) => [pressed && { opacity: 0.6 }, styles.headerAction]}
            >
              <Text style={[styles.headerActionText, { color: theme.colors.accent.indigo }]}>
                {leaderboardStrings.addColumnCta}
              </Text>
            </Pressable>
          ) : null}
          {populated !== null && onAddEntry !== undefined ? (
            <Pressable
              onPress={onAddEntry}
              accessibilityRole="button"
              accessibilityLabel={leaderboardStrings.addEntryCta}
              style={({ pressed }) => [pressed && { opacity: 0.6 }, styles.headerAction]}
            >
              <Text style={[styles.headerActionText, { color: theme.colors.accent.indigo }]}>
                {leaderboardStrings.addEntryCta}
              </Text>
            </Pressable>
          ) : null}
          {onOpenOptions !== undefined ? (
            <Pressable
              onPress={onOpenOptions}
              accessibilityRole="button"
              accessibilityLabel="More options"
              style={({ pressed }) => [pressed && { opacity: 0.6 }, styles.headerAction]}
            >
              <Text style={[styles.headerActionText, { color: theme.colors.accent.indigo }]}>
                ⋯
              </Text>
            </Pressable>
          ) : null}
        </View>
      </View>
      {renderBody()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  } satisfies ViewStyle,
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  headerActions: {
    flexDirection: 'row',
  } satisfies ViewStyle,
  headerAction: {
    marginLeft: 12,
  } satisfies ViewStyle,
  headerActionText: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  title: {
    fontSize: 17,
    fontWeight: '600',
    flex: 1,
    marginRight: 12,
  } satisfies TextStyle,
  headerRow: {
    flexDirection: 'row',
  } satisfies ViewStyle,
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  } satisfies ViewStyle,
  errorTitle: {
    fontSize: 17,
    fontWeight: '600',
    marginBottom: 12,
    textAlign: 'center',
  } satisfies TextStyle,
  retryButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  } satisfies ViewStyle,
  retryText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  } satisfies TextStyle,
});
